import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { hook, withValue } from '@g4/automation-hooks';

import { formatStringForHookValue } from '../../../utils/automationHookUtils';
import { Layout, useMedia } from '../../Boneyard';

import ExpandableMenuItem from './navGroup/ExpandableMenuItem';
import NavItem from './navGroup/NavItem';

const propTypes = {
  forceExpanded: PropTypes.bool,
  items: PropTypes.array,
  isMegaMenu: PropTypes.bool,
  displayAsColumn: PropTypes.bool,
  displayAsDropdown: PropTypes.bool,
  isMegaMenuLink: PropTypes.bool,
  defaultBackgroundType: PropTypes.oneOf(['light', 'dark']),
  automationHook: PropTypes.string,
  isAllegiantStadiumNavItems: PropTypes.bool,
};

const defaultProps = {
  items: [],
  isMegaMenu: false,
  displayAsColumn: false,
  displayAsDropdown: false,
  isMegaMenuLink: false,
  defaultBackgroundType: 'light',
  isAllegiantStadiumNavItems: false,
};

const MegaMenuContainer = styled.ul`
  display: block;
  padding: 0;

  ${props => props.theme.query.md} {
    padding: ${props => props.theme.spacing(6)} 0;
    display: flex;
    justify-content: stretch;
    width: 100%;
  }
`;

const MegaMenuGroup = styled.div`
  display: block;
  border-right: 0;
  padding: 0;

  ${props => props.theme.query.md} {
    flex-basis: 25%;
    flex-grow: 1;
    border-right: 1px solid
      ${props => props.theme.colors.siteHeader.megaMenu.separator};
    padding: 0 ${props => props.theme.spacing(6)};

    :first-of-type {
      padding-left: 0;
    }

    :last-of-type {
      border-right: none;
      padding-right: 0;
    }
  }
`;

const NavContainer = styled.ul`
  display: flex;
  flex-direction: ${props => (props.displayAsColumn ? 'column' : 'row')};
  align-items: ${props => (props.displayAsColumn ? 'flex-start' : 'flex-end')};

  ${props =>
    props.displayAsDropdown &&
    `
    background: ${props.theme.colors.siteHeader.light.linkBackground};
  `}
`;

const NavListItem = styled.li`
  display: flex;
`;

const GroupMenuItem = ({ children }) => {
  return children({ displayAsColumn: true });
};

const NavGroup = ({
  items,
  isMegaMenu,
  displayAsColumn,
  displayAsDropdown,
  isMegaMenuLink,
  defaultBackgroundType,
  isAllegiantStadiumNavItems,
  forceExpanded,
  automationHook,
}) => {
  const mq = useMedia();
  const isMobile = mq.lte('sm');
  const getMenuItem = (item, automationHook) => {
    if (item.__typename === 'ExpandableMenuItem') {
      return (
        <ExpandableMenuItem
          item={item}
          defaultBackgroundType={defaultBackgroundType}
          isAllegiantStadiumNavItems={isAllegiantStadiumNavItems}
          forceExpanded={forceExpanded}
          automationHook={withValue(
            `${automationHook}-item`,
            formatStringForHookValue(item.title)
          )}
        >
          {props =>
            item.items && item.items.length ? (
              <NavGroup
                items={item.items}
                automationHook={automationHook}
                {...props}
              />
            ) : null
          }
        </ExpandableMenuItem>
      );
    }

    if (item.__typename === 'GroupMenuItem') {
      return (
        <GroupMenuItem item={item}>
          {props =>
            item.items && item.items.length ? (
              <NavGroup
                items={item.items}
                forceExpanded={!isMobile}
                automationHook={automationHook}
                {...props}
              />
            ) : null
          }
        </GroupMenuItem>
      );
    }

    return (
      <NavItem
        item={item}
        onclick={item.onclick}
        defaultBackgroundType={defaultBackgroundType}
        isAllegiantStadiumNavItems={isAllegiantStadiumNavItems}
        isMegaMenuLink={isMegaMenuLink}
        automationHook={withValue(
          `${automationHook}-item`,
          formatStringForHookValue(item.title)
        )}
      />
    );
  };

  if (isMegaMenu) {
    return (
      <Layout.Strip color="white">
        <MegaMenuContainer {...hook(automationHook)}>
          {items.map(item => (
            <MegaMenuGroup key={item.id}>
              <NavListItem>{getMenuItem(item, automationHook)}</NavListItem>
            </MegaMenuGroup>
          ))}
        </MegaMenuContainer>
      </Layout.Strip>
    );
  }

  return (
    <NavContainer
      displayAsColumn={displayAsColumn}
      displayAsDropdown={displayAsDropdown}
      {...hook(automationHook)}
    >
      {items.map(item => (
        <NavListItem key={item.id}>
          {getMenuItem(item, automationHook)}
        </NavListItem>
      ))}
    </NavContainer>
  );
};

NavGroup.propTypes = propTypes;
NavGroup.defaultProps = defaultProps;

export default NavGroup;
